import React from 'react'
import { HashRouter as Router, Routes, Route } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { AuthProvider, useAuth } from './components/auth/AuthProvider'
import LoginForm from './components/auth/LoginForm'
import OnboardingTour from './components/onboarding/OnboardingTour'
import FeatureHighlight from './components/onboarding/FeatureHighlight'
import Navbar from './components/Navbar'
import Dashboard from './components/Dashboard'
import HabitList from './components/HabitList'
import VisionBoard from './components/VisionBoard'
import Analytics from './components/Analytics'
import ProductRecommendations from './components/ProductRecommendations'
import Reflections from './components/Reflections'
import Reminders from './components/Reminders'
import Settings from './components/Settings'
import './App.css'

const AppContent = () => {
  const { user, loading, showOnboarding, login, completeOnboarding, skipOnboarding } = useAuth()

  const featureHighlights = [
    {
      title: 'Welcome to DreamForge!',
      description: 'Start by creating your first habit or vision. Small actions lead to big transformations.',
      icon: 'FiTarget',
      gradient: 'from-forge-400 to-orange-500'
    },
    {
      title: 'Track Your Progress',
      description: 'Use the analytics page to see patterns and optimize your performance.',
      icon: 'FiBarChart3',
      gradient: 'from-blue-400 to-indigo-500'
    },
    {
      title: 'Stay Consistent',
      description: 'Set up reminders to never miss a habit. Consistency is the key to success.',
      icon: 'FiBell',
      gradient: 'from-emerald-400 to-green-500'
    }
  ]

  console.log('AppContent: Current state:', { user: !!user, loading, showOnboarding });

  if (loading) {
    return (
      <div className="min-h-screen bg-underworld-gradient flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-16 h-16 border-4 border-forge-400/30 border-t-forge-400 rounded-full animate-spin mb-4"></div>
          <h3 className="text-xl font-bold text-forge-400 mb-2">Loading DreamForge</h3>
          <p className="text-forge-300">Preparing your forge...</p>
        </motion.div>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-underworld-gradient">
        <LoginForm onLogin={login} />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-underworld-gradient">
      <Navbar />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/habits" element={<HabitList />} />
          <Route path="/vision" element={<VisionBoard />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/products" element={<ProductRecommendations />} />
          <Route path="/reflections" element={<Reflections />} />
          <Route path="/reminders" element={<Reminders />} />
          <Route path="/settings" element={<Settings />} />
        </Routes>
      </motion.div>

      {/* Onboarding Tour */}
      <AnimatePresence>
        {showOnboarding && (
          <OnboardingTour
            onComplete={completeOnboarding}
            onSkip={skipOnboarding}
          />
        )}
      </AnimatePresence>

      {/* Feature Highlights for returning users */}
      {user && !user.isNewUser && !showOnboarding && (
        <FeatureHighlight
          features={featureHighlights}
          onComplete={() => {}}
          autoAdvance={true}
          interval={6000}
        />
      )}
    </div>
  )
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  )
}

export default App